import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, IconButton, Typography, Container } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { Fade } from "react-awesome-reveal";
import img from '../assets/images/img1.jpg';
import img2 from '../assets/images/img2.jpg';
import img3 from '../assets/images/img3.jpg';
import img4 from '../assets/images/img4.jpg';
import img5 from '../assets/images/img5.jpg';

function Carousel() {
    const [positionIndexes, setPositionIndexes] = useState([0, 1, 2, 3, 4]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isHovered) {
                handleNext();
            }
        }, 4000);
        return () => clearInterval(interval);
    }, [isHovered]);

    const handleNext = () => {
        setPositionIndexes((prev) => {
            const next = prev.map((item) => (item - 1 + 5) % 5);
            return next;
        });
        setActiveIndex((prev) => (prev + 1) % 5);
    };

    const handlePrev = () => {
        setPositionIndexes((prev) => {
            const next = prev.map((item) => (item + 1) % 5);
            return next;
        });
        setActiveIndex((prev) => (prev - 1 + 5) % 5);
    };

    const images = [
        { src: img, label: 'Authentic Flavors' },
        { src: img2, label: 'Korean Street Food' },
        { src: img3, label: 'Fresh Sushi' },
        { src: img4, label: 'Korean BBQ' },
        { src: img5, label: 'Dining Experience' }
    ];
    const positions = ['center', 'left1', 'left', 'right', 'right1'];

    const ImageVariants = {
        center: {
            x: '0%',
            scale: 1,
            zIndex: 5,
            opacity: 1,
            filter: 'brightness(100%)',
            transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
        },
        left1: {
            x: '-50%',
            scale: 0.85,
            zIndex: 3,
            opacity: 0.7,
            filter: 'brightness(60%)',
            transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
        },
        left: {
            x: '-90%',
            scale: 0.7,
            zIndex: 2,
            opacity: 0.5,
            filter: 'brightness(40%)',
            transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
        },
        right: {
            x: '90%',
            scale: 0.7,
            zIndex: 2,
            opacity: 0.5,
            filter: 'brightness(40%)',
            transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
        },
        right1: {
            x: '50%',
            scale: 0.85,
            zIndex: 3,
            opacity: 0.7,
            filter: 'brightness(60%)',
            transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] }
        }
    };

    const handleDotClick = (clickedIndex) => {
        if (clickedIndex === activeIndex) return;

        const diff = clickedIndex - activeIndex;
        const absValue = Math.abs(diff);
        const shortestPath = absValue > 2 ? 5 - absValue : absValue;

        if (
            (diff > 0 && absValue <= 2) ||
            (diff < 0 && absValue > 2)
        ) {
            for (let i = 0; i < shortestPath; i++) {
                handleNext();
            }
        } else {
            for (let i = 0; i < shortestPath; i++) {
                handlePrev();
            }
        }
    };

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, rgba(10, 10, 10, 0.98), rgba(20, 20, 20, 0.98))',
                py: { xs: 8, md: 10 },
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent, rgba(255,77,77,0.5), rgba(249,203,40,0.5), transparent)'
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '-10%',
                    width: '500px',
                    height: '500px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(249, 203, 40, 0.06), transparent 70%)',
                    filter: 'blur(80px)',
                    pointerEvents: 'none',
                }
            }}
        >
            {/* Decorative Grid Pattern */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        linear-gradient(90deg, rgba(255,77,77,0.02) 1px, transparent 1px),
                        linear-gradient(rgba(255,77,77,0.02) 1px, transparent 1px)
                    `,
                    backgroundSize: '60px 60px',
                    opacity: 0.3,
                    pointerEvents: 'none',
                }}
            />

            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <Fade direction="down" triggerOnce duration={1000}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                mb: 2
                            }}
                        >
                            <Box
                                sx={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 8px 24px rgba(255, 77, 77, 0.3)',
                                }}
                            >
                                <PhotoLibraryIcon sx={{ fontSize: '24px', color: '#fff' }} />
                            </Box>
                        </Box>
                        <Box sx={{
                            width: { xs: '60px', md: '80px' },
                            height: '4px',
                            background: 'linear-gradient(90deg, #FF4D4D, #F9CB28)',
                            margin: '0 auto 2rem',
                            borderRadius: '2px'
                        }} />
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                fontWeight: 900,
                                background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                marginBottom: 1.5,
                                letterSpacing: '1px'
                            }}
                        >
                            Gallery Showcase
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontSize: { xs: '0.95rem', md: '1.1rem' },
                                color: 'rgba(255,255,255,0.6)',
                                maxWidth: '600px',
                                margin: '0 auto',
                                lineHeight: 1.7
                            }}
                        >
                            Discover our culinary artistry through a visual journey of authentic Asian flavors and memorable dining moments
                        </Typography>
                    </Fade>
                </Box>

                {/* Carousel */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: { xs: '280px', sm: '380px', md: '450px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'visible',
                        mt: { xs: 4, sm: 5 },
                        mb: { xs: 5, md: 6 }
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <AnimatePresence>
                        {images.map((image, index) => (
                            <motion.div
                                key={index}
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    maxWidth: '380px',
                                    height: '100%',
                                    maxHeight: '380px',
                                }}
                                initial="center"
                                animate={positions[positionIndexes[index]]}
                                variants={ImageVariants}
                                transition={{ duration: 0.6 }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        border: positions[positionIndexes[index]] === 'center'
                                            ? '2px solid rgba(255, 77, 77, 0.3)'
                                            : '1px solid rgba(255, 255, 255, 0.1)',
                                        boxShadow: positions[positionIndexes[index]] === 'center'
                                            ? '0 20px 60px rgba(255, 77, 77, 0.3)'
                                            : '0 8px 24px rgba(0,0,0,0.4)',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: positions[positionIndexes[index]] === 'center' ? 'scale(1.02)' : 'none',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 0,
                                            background: positions[positionIndexes[index]] === 'center'
                                                ? 'linear-gradient(135deg, rgba(255, 77, 77, 0.1), rgba(249, 203, 40, 0.1))'
                                                : 'transparent',
                                            pointerEvents: 'none',
                                            zIndex: 1,
                                        }
                                    }}
                                >
                                    <img
                                        src={image.src}
                                        alt={image.label}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    {/* Image Label */}
                                    {positions[positionIndexes[index]] === 'center' && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                padding: '20px',
                                                background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                                                zIndex: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                                                    fontWeight: 700,
                                                    textAlign: 'center',
                                                    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                                                }}
                                            >
                                                {image.label}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {/* Navigation Buttons */}
                    <IconButton
                        onClick={handlePrev}
                        sx={{
                            position: 'absolute',
                            left: { xs: '-4px', md: '3%' },
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            background: 'rgba(255, 255, 255, 0.08)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            width: { xs: '44px', md: '52px' },
                            height: { xs: '44px', md: '52px' },
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, rgba(255, 77, 77, 0.3), rgba(249, 203, 40, 0.3))',
                                transform: 'translateY(-50%) translateX(-4px)',
                                borderColor: 'rgba(255, 77, 77, 0.6)',
                                boxShadow: '0 8px 24px rgba(255, 77, 77, 0.3)',
                            }
                        }}
                    >
                        <ArrowBackIosNewIcon
                            sx={{
                                color: 'white',
                                fontSize: { xs: 20, md: 24 }
                            }}
                        />
                    </IconButton>

                    <IconButton
                        onClick={handleNext}
                        sx={{
                            position: 'absolute',
                            right: { xs: '-4px', md: '3%' },
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            background: 'rgba(255, 255, 255, 0.08)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.15)',
                            width: { xs: '44px', md: '52px' },
                            height: { xs: '44px', md: '52px' },
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, rgba(255, 77, 77, 0.3), rgba(249, 203, 40, 0.3))',
                                transform: 'translateY(-50%) translateX(4px)',
                                borderColor: 'rgba(255, 77, 77, 0.6)',
                                boxShadow: '0 8px 24px rgba(255, 77, 77, 0.3)',
                            }
                        }}
                    >
                        <ArrowForwardIosIcon
                            sx={{
                                color: 'white',
                                fontSize: { xs: 20, md: 24 }
                            }}
                        />
                    </IconButton>
                </Box>

                {/* Pagination Dots */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: { xs: '8px', md: '10px' },
                        mt: { xs: 2, md: 3 }
                    }}
                >
                    {images.map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => handleDotClick(index)}
                            sx={{
                                width: activeIndex === index ? { xs: '32px', md: '40px' } : { xs: '10px', md: '12px' },
                                height: { xs: '10px', md: '12px' },
                                borderRadius: '20px',
                                background: activeIndex === index
                                    ? 'linear-gradient(135deg, #FF4D4D, #F9CB28)'
                                    : 'rgba(255, 255, 255, 0.2)',
                                cursor: 'pointer',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                border: activeIndex === index ? 'none' : '1px solid rgba(255,255,255,0.15)',
                                position: 'relative',
                                boxShadow: activeIndex === index
                                    ? '0 4px 12px rgba(255, 77, 77, 0.4)'
                                    : 'none',
                                '&:hover': {
                                    background: activeIndex === index
                                        ? 'linear-gradient(135deg, #FF4D4D, #F9CB28)'
                                        : 'rgba(255, 255, 255, 0.4)',
                                    transform: 'scale(1.15)',
                                    boxShadow: activeIndex === index
                                        ? '0 6px 16px rgba(255, 77, 77, 0.5)'
                                        : '0 2px 8px rgba(255, 255, 255, 0.2)',
                                }
                            }}
                        />
                    ))}
                </Box>

                {/* Counter */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 3,
                        gap: 1
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            color: 'rgba(255,255,255,0.5)',
                            fontWeight: 500,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontWeight: 700,
                                fontSize: { xs: '1rem', md: '1.1rem' }
                            }}
                        >
                            {activeIndex + 1}
                        </Box>
                        {' '} / {images.length}
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}

export default Carousel;
